import type { AllPlanScheduleType } from '@demind-inc/core';

import type { SuggestAllAIPlanState } from '../data-access/recoil/suggestAllAIPlan.atom';
import {
  AllPlanHomeRestoreResult,
  AutoAllPlanCacheStore,
  buildAllPlanHomeRestore,
  readPlanCacheStore,
} from './allPlanCache.helpers';

/** Shared across hook instances so remounts don't re-hydrate over an in-flight run. */
let restoredCacheKey: string | null = null;
let lastAutoRunKey: string | null = null;

export const getLastAutoRunKey = () => lastAutoRunKey;
export const setLastAutoRunKey = (key: string | null) => {
  lastAutoRunKey = key;
};

export const getRestoredCacheKey = () => restoredCacheKey;
export const setRestoredCacheKey = (key: string | null) => {
  restoredCacheKey = key;
};

/** Test-only: reset module guards between cases. */
export const __resetAllPlanHomeBootstrapGuardsForTests = () => {
  restoredCacheKey = null;
  lastAutoRunKey = null;
};

export type ApplyHomeRestoreResult = {
  applied: boolean;
  restore: AllPlanHomeRestoreResult;
  store: AutoAllPlanCacheStore;
};

/**
 * Hydrate Plan My Day atom + plannedTypes from the multi-date cache when the
 * restore key changes. Skips while a run is in flight so remounting the panel
 * cannot flash empty results / clear isLoading.
 */
export const applyAllPlanHomeRestoreIfNeeded = ({
  today,
  previousTargetDate,
  isLoading,
  deferForPendingJob = false,
  setPlannedTypes,
  setSuggestAllAIPlanState,
}: {
  today: string;
  previousTargetDate: string | null | undefined;
  isLoading: boolean;
  /** An in-flight Plan My Day job is being resumed. Hydrating the cache here
   * would paint the previous job's result until that run finishes. */
  deferForPendingJob?: boolean;
  setPlannedTypes: (types: AllPlanScheduleType[]) => void;
  setSuggestAllAIPlanState: (
    updater: (prev: SuggestAllAIPlanState) => SuggestAllAIPlanState
  ) => void;
}): ApplyHomeRestoreResult => {
  const store = readPlanCacheStore();
  const restore = buildAllPlanHomeRestore({
    store,
    today,
    previousTargetDate,
  });

  if (restoredCacheKey === restore.restoreKey) {
    return { applied: false, restore, store };
  }

  // Leave the key unset while loading or while a job is still in flight, so a
  // later idle mount can hydrate once that run has settled.
  if (isLoading || deferForPendingJob) {
    return { applied: false, restore, store };
  }

  restoredCacheKey = restore.restoreKey;
  setPlannedTypes(restore.plannedTypes);
  setSuggestAllAIPlanState((prev) => {
    if (prev.isLoading) {
      return prev;
    }
    return {
      ...prev,
      ...restore.patch,
      statusLines: restore.hasAnyPlan ? prev.statusLines : restore.patch.statusLines,
    };
  });

  return { applied: true, restore, store };
};

/**
 * Skip today-scoped Home bootstrap runs (daily defaults and auto-run) while
 * Plan My Day is showing a future date. Those runners set selectedDate and
 * the plan target to today, which would yank an active tomorrow plan back.
 */
export const shouldSkipAutoRunForActiveFuturePlan = ({
  today,
  activeTargetDate,
  isLoading,
  schedulesLength,
}: {
  today: string;
  activeTargetDate: string | null | undefined;
  isLoading: boolean;
  schedulesLength: number;
}): boolean =>
  Boolean(
    activeTargetDate &&
      activeTargetDate !== today &&
      (isLoading || schedulesLength > 0)
  );
